/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {Fragment, useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Button,
    Image,
    Flex,
    Box,
    Text,
    Divider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import ErrorSources from '../../../components/error-sources'
import Link from '../../../components/link'
import {getUrl} from '../../../utils/cloudinary-utils'
/* -----------------Adyen Begin ------------------------ */
import '@adyen/adyen-salesforce-pwa/dist/app/adyen.css'
import {useAccessToken, useCustomerId} from '@salesforce/commerce-sdk-react'
import {useCommerceApi} from '@salesforce/commerce-sdk-react'
import {AdyenExpressCheckoutProvider, ApplePayExpress} from '../../../adyen'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import PayPalExpress from '../../../adyen/lib/components/paypalExpress'
import CustomApi from '../../../hooks/use-custom-api'
import {useGlobaleContext} from '../../../contexts/globale'
import {scheduleAfterPaint} from '../../../utils/common-utils'
/* -----------------Adyen End ------------------------ */

const CartCta = ({
    isDesktopOnly,
    hasError = false,
    errorSources = null,
    isLoading = false,
    isCustomerEmployee = false,
    selectedDelivery,
    showExpressCheckout = false,
    isTopCartCta = false
}) => {
    const display = isDesktopOnly ? {base: 'none', lg: 'inline-flex'} : 'inline-flex'
    const [showPaypalError, setShowPaypalError] = useState(false)
    const lockIconSrc = !hasError
        ? getUrl('UIImages/images/lock-icon.svg')
        : getUrl('UIImages/images/disabled-lock-icon.svg')

    const customerId = useCustomerId()
    const {getTokenWhenReady} = useAccessToken()
    const api = useCommerceApi()
    const {proxy} = api.shopperExperience.clientConfig
    const customapi = new CustomApi()
    const navigate = useNavigation()
    const {locale, site} = useMultiSite()
    const {data: basket} = useCurrentBasket()
    const {countryISO} = useGlobaleContext()

    const [authToken, setAuthToken] = useState()
    const [expressCheckoutAvailability, setExpressCheckoutAvailability] = useState({
        paypalExpressEnabled: false,
        applePayExpressEnabled: false
    })
    const hasFetchedExpressAvailability = useRef(false)

    useEffect(() => {
        const getToken = async () => {
            const token = await getTokenWhenReady()
            setAuthToken(token)
        }

        getToken()
    }, [])

    useEffect(() => {
        if (!showExpressCheckout || !authToken || hasFetchedExpressAvailability.current) {
            return
        }
        hasFetchedExpressAvailability.current = true

        const fetchExpressCheckoutAvailability = async () => {
            try {
                const response = await customapi.get(
                    authToken,
                    'express-checkout-availability',
                    '/',
                    {},
                    proxy
                )
                setExpressCheckoutAvailability({
                    paypalExpressEnabled: !!response?.paypalExpressEnabled,
                    applePayExpressEnabled: !!response?.applePayExpressEnabled
                })
            } catch (e) {
                setExpressCheckoutAvailability({
                    paypalExpressEnabled: false,
                    applePayExpressEnabled: false
                })
                console.warn("Error occurred in 'express-checkout-availability'", e.message)
            }
        }

        fetchExpressCheckoutAvailability()
    }, [showExpressCheckout, authToken, proxy])

    const dividerProps = {
        orientation: 'horizontal',
        borderColor: 'gray.400',
        marginTop: '12px',
        width: '-webkit-fill-available',
        marginLeft: '0px',
        marginRight: '0px'
    }
    const isGlobalEOperatedCountry = !!countryISO && countryISO !== 'US'
    const canShowApplePayExpress =
        showExpressCheckout &&
        expressCheckoutAvailability.applePayExpressEnabled &&
        !basket?.giftCertificateItems?.length
    const canShowPaypalExpress =
        showExpressCheckout &&
        expressCheckoutAvailability.paypalExpressEnabled &&
        !isCustomerEmployee &&
        !isGlobalEOperatedCountry
    const hasAnyExpressButton = canShowApplePayExpress || canShowPaypalExpress
    const handleCheckout = (event) => {
        if (hasError) {
            event.preventDefault()
            event.stopPropagation()
            return
        }

        if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return
        }

        event.preventDefault()
        scheduleAfterPaint(() => {
            window.location.href = '/checkout'
        })
    }

    return (
        <Fragment>
            {canShowPaypalExpress && showPaypalError && (
                <Box
                    className="express-paypal-error"
                    backgroundColor="peach.100"
                    borderRadius="3px"
                    padding="15px"
                >
                    <Text fontSize="16px" lineHeight="25px" fontWeight="400" color="black.100">
                        <FormattedMessage
                            id="paypal.error"
                            defaultMessage="We're sorry. This order cannot be completed with Paypal, please select a different payment method to checkout."
                        />
                    </Text>
                </Box>
            )}
            <Box
                className="checkout-buttons"
                gap={{base: '15px', lg: '10px'}}
                margin={{base: '10px 0', lg: '0'}}
                display={isTopCartCta ? 'flex' : 'inline-grid'}
                sx={{
                    '.paypal-container-wrapper': {
                        flex: '1',
                        width: '100%',
                        height: '50px',
                        borderRadius: '10px',
                        overflow: 'hidden'
                    },
                    '.adyen-checkout__paypal__button': {
                        margin: '0'
                    }
                }}
            >
                <Button
                    as={Link}
                    isExternal={true}
                    to="/checkout"
                    isDisabled={hasError && !isLoading}
                    isLoading={isLoading}
                    aria-disabled={hasError}
                    onClick={handleCheckout}
                    tabIndex={hasError ? -1 : undefined}
                    width="100%"
                    marginTop="0"
                    leftIcon={<Image src={lockIconSrc} width="15px" height="15px" alt="Checkout" />}
                    variant="solid"
                    height="56px"
                    fontSize="17px"
                    display={display}
                    gap="2px"
                    mb="0px"
                    color={!hasError ? 'white' : 'gray.40'}
                >
                    <FormattedMessage defaultMessage="Checkout" id="cart_cta.btn.checkout" />
                </Button>
                {hasAnyExpressButton && !hasError && (
                    <Box className="or-divider" display="inline-flex" gap="15px">
                        <Divider {...dividerProps} />
                        <Text fontSize="13px" lineHeight="22px" fontWeight="300" color="black.100">
                            <FormattedMessage id="or.express.checkout" defaultMessage="Or" />
                        </Text>
                        <Divider {...dividerProps} />
                    </Box>
                )}
                {hasAnyExpressButton && (
                    <Flex
                        justify={'center'}
                        gap="10px"
                        display={hasError && !isLoading ? 'none' : 'flex'}
                        sx={{
                            '.apple-pay-wrapper': {
                                width: '50%'
                            }
                        }}
                    >
                        <AdyenExpressCheckoutProvider
                            authToken={authToken}
                            customerId={customerId}
                            locale={locale}
                            isBopis={selectedDelivery === 'storepickup'}
                            site={site}
                            basket={basket}
                            navigate={navigate}
                        >
                            {canShowApplePayExpress && basket && (
                                <ApplePayExpress key={basket.orderTotal} isCheckout={false} />
                            )}
                            {canShowPaypalExpress && (
                                <PayPalExpress
                                    basket={basket}
                                    authToken={authToken}
                                    customerId={customerId}
                                    locale={locale}
                                    site={site}
                                    setShowPaypalError={(bool) => {
                                        setShowPaypalError(bool)
                                    }}
                                    navigate={navigate}
                                    isBopis={selectedDelivery === 'storepickup'}
                                    isCheckout={false}
                                />
                            )}
                        </AdyenExpressCheckoutProvider>
                    </Flex>
                )}
            </Box>
            {hasError && errorSources ? (
                <ErrorSources errorSources={errorSources} dataTestId="sf-cart-cta-error" />
            ) : null}
        </Fragment>
    )
}

CartCta.propTypes = {
    isDesktopOnly: PropTypes.bool,
    isLoading: PropTypes.bool,
    hasError: PropTypes.bool,
    showExpressCheckout: PropTypes.bool,
    isCustomerEmployee: PropTypes.bool,
    selectedDelivery: PropTypes.string,
    errorSources: PropTypes.arrayOf(
        PropTypes.shape({
            hasError: PropTypes.bool,
            error: PropTypes.object,
            fallback: PropTypes.string
        })
    ),
    isTopCartCta: PropTypes.bool
}

export default CartCta
